import { tr } from '@/lib/i18n-core';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { FileText } from 'lucide-react';
import { get, qs } from '@/lib/api';
import { dateTime, humanize } from '@/lib/utils';
import { printDocument } from '@/lib/print';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, SkeletonText, StatusBadge } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

interface LabItemRow {
  id: string;
  status: string;
  test: { name: string; unit: string | null };
  results: { value: string; unit: string | null; flag: string }[];
  resultHidden?: boolean;
  rejected: { reason: string | null } | null;
}

export interface InvestigationRow {
  kind: 'lab' | 'imaging' | 'outside';
  id: string;
  orderedAt: string;
  number?: string;
  priority?: string;
  status?: string;
  collectAt?: string | null;
  items?: LabItemRow[] | { label: string; notes: string | null }[];
  studyType?: { name: string; modality: string };
  reportImpression?: string | null;
  resultHidden?: boolean;
  type?: string;
}

export interface MovedRequest {
  id: string;
  kind: 'lab' | 'radiology';
  label: string;
  requestedAt: string;
}

export interface InvestigationPage {
  data: InvestigationRow[];
  nextCursor: string | null;
  moved: MovedRequest[];
}

interface TrendSeries {
  code: string;
  name: string;
  unit: string | null;
  points: { at: string; value: number; flag: string }[];
}

export function investigationsQueryKey(path: string): unknown[] {
  return ['investigations', path];
}

export function cumulativeColumns(series: TrendSeries[], days = 7): { days: string[]; rows: { name: string; unit: string | null; cells: (string | null)[] }[] } {
  const allDays = [...new Set(series.flatMap((s) => s.points.map((p) => p.at.slice(0, 10))))].sort().slice(-days);
  return {
    days: allDays,
    rows: series.map((s) => ({
      name: s.name,
      unit: s.unit,
      cells: allDays.map((day) => {
        const last = [...s.points].filter((p) => p.at.slice(0, 10) === day).pop();
        return last ? String(last.value) : null;
      }),
    })),
  };
}

function flagClass(flag: string): string {
  if (flag.startsWith('critical')) return 'font-semibold text-destructive';
  if (flag !== 'normal' && flag !== 'no_range') return 'font-medium text-warning';
  return '';
}

export function InvestigationList({
  path,
  patientId,
  onReorder,
  onDismiss,
  showCumulative = false,
}: {
  path: string;
  patientId: string;
  onReorder?: (request: MovedRequest) => void;
  onDismiss?: (request: MovedRequest) => void;
  showCumulative?: boolean;
}) {
  const toast = useToast();
  const pages = useInfiniteQuery({
    queryKey: investigationsQueryKey(path),
    queryFn: ({ pageParam }) => get<InvestigationPage>(`${path}${qs({ limit: 20, cursor: (pageParam as string | undefined) || undefined })}`),
    initialPageParam: '' as string,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const trends = useQuery({
    queryKey: ['lab-trends', patientId],
    queryFn: () => get<TrendSeries[]>(`/patients/${patientId}/lab-trends`),
    enabled: showCumulative,
  });

  const rows = (pages.data?.pages ?? []).flatMap((page) => page.data);
  const moved = pages.data?.pages[0]?.moved ?? [];

  const openReport = async (kind: 'lab' | 'imaging', id: string) => {
    try {
      const doc = await get<{ html: string }>(kind === 'lab' ? `/lab/orders/${id}/print` : `/radiology/studies/${id}/print`);
      printDocument(doc.html);
    } catch (caught) {
      toast.error(tr('opd', 'could_not_open_the_report'), caught instanceof Error ? caught.message : undefined);
    }
  };

  const cumulative = showCumulative && trends.data && trends.data.length > 0 ? cumulativeColumns(trends.data) : null;

  return (
    <div className="space-y-4">
      {moved.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">{tr('opd', 'requests_made_before')} {dateTime(moved[0].requestedAt)} {tr('opd', 'that_never_reached_the_lab')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p className="text-muted-foreground">{tr('opd', 'these_were_entered_as_medicine_orders_and_never')}</p>
            {moved.map((request) => (
              <div key={request.id} className="flex items-center justify-between gap-2 rounded-md border p-2">
                <span>
                  {request.label} <Badge variant="secondary">{request.kind === 'lab' ? tr('opd', 'laboratory_2') : tr('opd', 'imaging_2')}</Badge>
                </span>
                <span className="flex gap-2">
                  {onReorder && (
                    <Button size="sm" onClick={() => onReorder(request)}>
                      {tr('opd', 're_order')}
                    </Button>
                  )}
                  {onDismiss && (
                    <Button size="sm" variant="ghost" onClick={() => onDismiss(request)}>
                      {tr('opd', 'dismiss')}
                    </Button>
                  )}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{tr('opd', 'investigations')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          {pages.isLoading && <SkeletonText lines={2} />}
          {!pages.isLoading && rows.length === 0 && <p className="text-muted-foreground">{tr('opd', 'nothing_ordered_yet')}</p>}
          {rows.map((row) => (
            <div key={`${row.kind}:${row.id}`} className="rounded-md border p-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-muted-foreground">
                  {row.kind === 'outside' ? tr('opd', 'outside_requisition') : <span className="font-mono">{row.number}</span>} · {dateTime(row.orderedAt)}
                  {row.priority && row.priority !== 'routine' ? ` · ${humanize(row.priority, 'priority')}` : ''}
                  {row.collectAt ? ` · collect at ${dateTime(row.collectAt)}` : ''}
                </p>
                {row.kind !== 'outside' && (
                  <Button size="sm" variant="ghost" onClick={() => void openReport(row.kind === 'lab' ? 'lab' : 'imaging', row.id)}>
                    <FileText className="me-1 h-4 w-4" /> {tr('opd', 'view_report')}
                  </Button>
                )}
              </div>
              {row.kind === 'lab' && (
                <ul className="mt-1 space-y-1">
                  {(row.items as LabItemRow[]).map((item) => (
                    <li key={item.id} className="flex items-center justify-between gap-2">
                      <span>{item.test.name}</span>
                      <span className="flex items-center gap-2">
                        {item.rejected ? (
                          <span className="text-xs text-destructive">{tr('opd', 'rejected_recollect')}{item.rejected.reason ? ` (${item.rejected.reason})` : ''}</span>
                        ) : item.resultHidden ? (
                          <span className="text-xs text-muted-foreground">{tr('opd', 'pending_verification')}</span>
                        ) : item.results[0] ? (
                          <span className={flagClass(item.results[0].flag)}>
                            {item.results[0].value} {item.results[0].unit ?? item.test.unit ?? ''}
                          </span>
                        ) : null}
                        <StatusBadge status={item.status} />
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {row.kind === 'imaging' && (
                <div className="mt-1 flex items-center justify-between gap-2">
                  <span>
                    {row.studyType?.name} <span className="text-xs text-muted-foreground">{row.studyType ? humanize(row.studyType.modality, 'radiology_modality') : ''}</span>
                    {row.resultHidden ? (
                      <span className="block text-xs text-muted-foreground">{tr('opd', 'report_pending_verification')}</span>
                    ) : (
                      row.reportImpression && <span className="block text-xs text-muted-foreground">{row.reportImpression}</span>
                    )}
                  </span>
                  {row.status && <StatusBadge status={row.status} />}
                </div>
              )}
              {row.kind === 'outside' && (
                <ul className="mt-1 list-disc ps-5">
                  {(row.items as { label: string; notes: string | null }[]).map((item, index) => (
                    <li key={index}>
                      {item.label}
                      {item.notes ? <span className="text-xs text-muted-foreground"> — {item.notes}</span> : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
          {pages.hasNextPage && (
            <Button variant="outline" size="sm" onClick={() => void pages.fetchNextPage()} loading={pages.isFetchingNextPage}>
              {tr('opd', 'load_more')}
            </Button>
          )}
        </CardContent>
      </Card>

      {cumulative && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">{tr('opd', 'cumulative_results_verified')}</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-start">{tr('opd', 'test')}</th>
                  {cumulative.days.map((day) => (
                    <th key={day} className="text-end font-mono text-xs">
                      {day.slice(5)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cumulative.rows.map((row) => (
                  <tr key={row.name}>
                    <td>
                      {row.name} <span className="text-xs text-muted-foreground">{row.unit ?? ''}</span>
                    </td>
                    {row.cells.map((cell, index) => (
                      <td key={index} className="text-end tabular-nums">
                        {cell ?? '—'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
